import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, type OnDestroy, computed, inject, signal } from '@angular/core';
import { Observable, Subscription, defer, from, of } from 'rxjs';
import { catchError, concatMap, finalize, map, tap } from 'rxjs/operators';

import type { MoverSolicitacaoRequest } from '@/core/api/sicom-api/servicos/kanban/kanban-servicos.model';
import { KanbanServicosService } from '@/core/api/sicom-api/servicos/kanban/kanban-servicos.service';
import { LoggerService } from '@/core/services/logger.service';
import { ConclusoesNoPortalService, chaveDaConclusao } from '@/features/servicos/shared/conclusoes-no-portal/conclusoes-no-portal.service';
import { DashboardServicosFacade } from '@/features/servicos/shared/dashboard-servicos/dashboard-servicos.facade';
import { chaveProtocolo } from '@/features/servicos/shared/kanban-protocolos/kanban-protocolos';
import type { ItemStatus } from '@/features/servicos/shared/models/item-status.model';
import { UfEfetivaStore } from '@/features/servicos/shared/uf-efetiva.store';
import { desfechoDaAcao } from '@/features/servicos/shared/utils/servicos-acoes.utils';
import { CustomService } from '@/shared/services/custom.service';

import { EEstadoDaFila, EEstadoDoItemDoLote, EMotivoDaParada, type IniciarLoteConfig, type ItemDoLote, type ResumoDoLote } from './encerramento-lote.model';

/** Requisição mínima que `finalizarSolicitacao` e `consultarDadosProtocoloPorServico` do facade aceitam para o par numero/ano/UF de um protocolo. */
interface RequestDoProtocolo {
    numeroProtocolo: string;
    anoProtocolo: string;
    ufCrm: string;
    codigoServico: string;
}

/** Mensagem-padrão quando o protocolo do lote não tem número/ano íntegros para gravar o movimento. */
const ERRO_IDENTIDADE_INVALIDA = 'Protocolo sem número/ano válidos: não é possível gravar o movimento.';

/**
 * Mensagem-padrão quando `executarDoZero`/`moverEFinalizar` são chamados com `somenteLeitura()`
 * verdadeiro. Ramo morto para o usuário regional (para quem `somenteLeitura()` é sempre `false`) e
 * hoje inalcançável pela UI também para o nacional — `colunaPodeIniciarLote` já esconde o botão que
 * abre este diálogo. Existe porque a UI é aparência, não garantia: ver o comentário de `executarDoZero`.
 */
const ERRO_SOMENTE_LEITURA = 'Perfil somente leitura: nao e permitido alterar o protocolo no portal.';

const CODIGO_PRE_MEDICO = 'pre-medico';
const CODIGO_PRE_PRESTADOR = 'pre-prestador';

const PENDENCIA_ENDERECO = 'endereço';
const PENDENCIA_TELEFONE = 'telefone';
const PENDENCIA_TELEFONE_PERDIDO_SEM_ENDERECO = 'telefone informado, mas perdido por falta de endereço';
const PENDENCIA_EMAIL_PRESTADOR = 'email do prestador';

function textoPreenchido(valor: unknown): boolean {
    return typeof valor === 'string' && valor.trim().length > 0;
}

/** Conta dígitos, não caracteres: `"(21) "` sem número nenhum não pode contar como telefone informado. */
function digitosPreenchidos(valor: unknown): boolean {
    return typeof valor === 'string' && valor.replace(/\D/g, '').length > 0;
}

/**
 * Rodada de correção 2 (achado "registrado, sem ação"): chamava-se `dtoReconhecivel`, nome que promete
 * mais do que a função entrega — ela só checa "não vazio", não se o formato é o esperado para o
 * serviço (um DTO com chaves de OUTRO serviço passa por aqui igual). O caso concreto que motivou a
 * função continua coberto: `{}` que `normalizarResponseConsulta` (`dashboard-servicos.facade.ts`)
 * devolve quando o portal responde algo que não é objeto (ex.: array). Um DTO vazio não é "cadastro
 * incompleto": é "não consegui verificar", e as duas coisas NUNCA podem virar a mesma pendência.
 */
function dtoNaoVazio(dto: Record<string, unknown>): boolean {
    return !!dto && Object.keys(dto).length > 0;
}

/**
 * Mesmo parsing de `chaveNumeroAno`/`chaveProtocolo` (`kanban-protocolos.ts`, não exportado de lá):
 * string vazia é AUSÊNCIA, nunca zero. `Number('')` vale `0` e `Number.isInteger(0)` é `true` — sem
 * este parsing, um protocolo sem `numeroProtocolo` passaria a guarda e o backend receberia `numero: 0`.
 */
function paraInteiro(valor?: string): number | null {
    const texto = (valor ?? '').trim();
    if (!texto) {
        return null;
    }
    const numero = Number(texto);
    return Number.isInteger(numero) ? numero : null;
}

/**
 * Pré-médico: regras conferidas em `PreMedicoService.salvarEnderecosETelefones` (`:280-310`), não
 * deduzidas do nome dos campos.
 *
 * O backend grava **DOIS** endereços por efetivação, cada um com o seu próprio `criarEndereco`, e a
 * pendência tem de refletir os dois:
 *
 * - **residencial**, dos campos `er_*` — nasce quando `er_logradouro` OU `er_cep` vem preenchido
 *   (`criarEndereco` só devolve `null` com os dois vazios);
 * - **correspondência**, dos campos `ec_*` — nasce pela MESMA regra sobre `ec_logradouro`/`ec_cep`, e
 *   `correspondenciaNoEnderecoComercial` ainda faz o comercial virar o de correspondência
 *   automaticamente quando não há residencial.
 *
 * Daí as regras:
 *
 * - **endereço**: só falta linha quando os QUATRO campos vêm vazios. O médico que preencheu só o
 *   endereço de trabalho tem endereço gravado — acusar "faltou: endereço" ali é falso, e o lote de
 *   quarenta em que isso acontece é um lote de quarenta pendências que ninguém vai ler.
 * - **telefone**: cada telefone é pendurado no endereço que o carrega, e por isso "informado" não
 *   basta — o que conta é se ALGUM chega a ser gravado. `er_telefone` e `celular_numero` só existem
 *   se o residencial nascer (`salvarTelefone` roda dentro do `if (residencial != null)`);
 *   `ec_telefone` só existe se o de correspondência nascer. Um celular informado com apenas o
 *   endereço comercial preenchido, portanto, se perde — e vira pendência de telefone, que é a
 *   verdade da base: não há telefone nenhum lá.
 * - **sem endereço nenhum, o telefone informado TAMBÉM se perde**: quando isso ocorre a pendência
 *   avisa os dois, não só o endereço.
 *
 * <p>Rodada de correção 2 (Menor 1): a pendência de **nacionalidade** foi REMOVIDA. Ela vinha do
 * fallback de `resolverTipoFormacao` (`:364-369`), mas ali nacionalidade vazia e `"B"` produzem o
 * MESMO `tipoFormacao = 6` — só `"E"` muda alguma coisa. Não há campo em branco na base nem dado
 * perdido: a regra acusava todo médico brasileiro cujo DTO não trouxesse o campo, e uma lista de 40
 * pendências falsas é o jeito mais eficiente de fazer ninguém ler a 41ª, que seria verdadeira.</p>
 */
function detectarPendenciasPreMedico(dto: Record<string, unknown>): string[] {
    const pendencias: string[] = [];

    const residencialGravado = textoPreenchido(dto['er_logradouro']) || textoPreenchido(dto['er_cep']);
    const comercialGravado = textoPreenchido(dto['ec_logradouro']) || textoPreenchido(dto['ec_cep']);
    const telefoneInformado = digitosPreenchidos(dto['er_telefone']) || digitosPreenchidos(dto['celular_numero']) || digitosPreenchidos(dto['ec_telefone']);
    // Só conta o telefone que TEM onde ser gravado: o backend pendura cada um no endereço de que ele veio.
    const telefoneGravado = (residencialGravado && (digitosPreenchidos(dto['er_telefone']) || digitosPreenchidos(dto['celular_numero']))) || (comercialGravado && digitosPreenchidos(dto['ec_telefone']));

    if (!residencialGravado && !comercialGravado) {
        pendencias.push(PENDENCIA_ENDERECO);
        if (telefoneInformado) {
            pendencias.push(PENDENCIA_TELEFONE_PERDIDO_SEM_ENDERECO);
        }
    } else if (!telefoneGravado) {
        pendencias.push(PENDENCIA_TELEFONE);
    }

    return pendencias;
}

/**
 * Pré-prestador: `PrePrestadorService.efetivar` NÃO grava endereço nem telefone — grava solicitante,
 * pessoa jurídica, e-mail de contato, prestador e histórico. As regras do pré-médico NÃO se aplicam
 * aqui; aplicá-las produziria pendência garantida sobre uma gravação que este serviço nunca faz. A
 * pendência real é `inserirEmailContatoSeNecessario`, que desiste em silêncio quando `emailPrestador`
 * vem vazio.
 */
function detectarPendenciasPrePrestador(dto: Record<string, unknown>): string[] {
    return textoPreenchido(dto['emailPrestador']) ? [] : [PENDENCIA_EMAIL_PRESTADOR];
}

/** Serviços cuja efetivação tem pendência de cadastro VERIFICADA na fonte. Fora daqui, nunca chutar. */
function servicoTemDeteccaoDePendencias(codigoServico: string): boolean {
    return codigoServico === CODIGO_PRE_MEDICO || codigoServico === CODIGO_PRE_PRESTADOR;
}

/**
 * Despacha a detecção pelo `codigoServico`: cada serviço grava (e falha em gravar) campos
 * diferentes, e aplicar a regra de um a outro é pior que não aplicar nenhuma. DTO vazio nunca vira
 * pendência — ver `dtoNaoVazio`.
 */
function detectarPendencias(codigoServico: string, dto: Record<string, unknown>): string[] {
    if (!dtoNaoVazio(dto)) {
        return [];
    }
    if (codigoServico === CODIGO_PRE_PRESTADOR) {
        return detectarPendenciasPrePrestador(dto);
    }
    if (codigoServico === CODIGO_PRE_MEDICO) {
        return detectarPendenciasPreMedico(dto);
    }
    return [];
}

/**
 * A fila do encerramento em lote: processa protocolo a protocolo, SEMPRE em série — nunca em
 * paralelo, porque pré-médico e pré-prestador geram o número de CRM por `MAX(NU_CRM)+1`, e dois
 * itens do MESMO lote em voo disputariam o mesmo número.
 *
 * Vive num serviço injetável, e não em estado do `KanbanProtocolos`, porque o quadro é destruído e
 * recriado a cada recarga (`carregandoProtocolos` troca o quadro por um spinner) — uma fila que
 * morasse ali morreria no primeiro item, levando junto a memória do que o portal já aceitou. Por
 * isso, também, **nenhuma recarga do quadro acontece aqui**: quem recarrega, uma única vez ao fim, é
 * a tarefa que abre e fecha o diálogo.
 *
 * `@Injectable()` sem `providedIn`, como o facade: é o `dashboard-servicos` quem o provê, no mesmo
 * nível — o nível que sobrevive à destruição do quadro.
 *
 * <p>Rodada de correção 1: `estadoDaFila === RODANDO` deixou de significar só "a fila automática do
 * `iniciar` está ativa" — passa a significar "alguma chamada ao portal deste lote está em voo", seja
 * ela da fila automática OU de um `repetir` manual. É essa generalização que fecha o Crítico 1 (duas
 * efetivações do mesmo lote em paralelo).</p>
 *
 * <p>Rodada de correção 2: essa generalização não bastava. `parar()` põe a fila em `PARADA`
 * IMEDIATAMENTE, sem esperar o item corrente terminar — então `estadoDaFila === RODANDO` deixou de
 * ser um proxy confiável para "há uma chamada ao portal aberta agora". `chamadaEmVoo` (abaixo) é o
 * sinal explícito para essa pergunta, independente do estado nominal da fila, e é o que `iniciar` e
 * `repetir` checam antes de tocar o portal de novo.</p>
 *
 * <p>Correção final (Crítico 2): a classe não tinha hook de destruição nenhum. Destruir o
 * `DashboardServicos` — um clique numa rota irmã basta — deixava a fila viva, chamando o portal item
 * após item sem tela, sem botão Parar e com os toasts de volta. Ver `ngOnDestroy`.</p>
 */
@Injectable()
export class EncerramentoLoteService implements OnDestroy {
    private readonly facade = inject(DashboardServicosFacade);
    private readonly kanbanServicosService = inject(KanbanServicosService);
    private readonly customService = inject(CustomService);
    private readonly logger = inject(LoggerService);
    /**
     * Onda de correção final (Crítico 1): nem este serviço nem o `EncerramentoLote` conheciam este
     * store — a porta ficava fechada só TRANSITIVAMENTE, porque `colunaPodeIniciarLote`
     * (`kanban-protocolos.ts`) já recusa o nacional e o botão que abre o diálogo nunca aparecia. Isso é
     * proteção por acidente de UI, não regra: ver `executarDoZero`/`moverEFinalizar`, onde a guarda de
     * verdade mora.
     */
    private readonly ufEfetivaStore = inject(UfEfetivaStore);
    /**
     * A memória de "o portal já aceitou este protocolo", compartilhada com o fluxo unitário do quadro.
     * Correção final (Crítico 4): antes o lote guardava esse fato SÓ em `ItemDoLote.aceitoNoPortal`,
     * que morre no próximo `iniciar()` e o quadro nunca leu — um item preso voltava ao quadro sem
     * marca e podia ser efetivado de novo. Ver `ConclusoesNoPortalService`.
     */
    private readonly conclusoesNoPortal = inject(ConclusoesNoPortalService);

    private readonly itensSignal = signal<ItemDoLote[]>([]);
    /** Protocolo a protocolo, na ordem em que a fila os processa. */
    readonly itens = this.itensSignal.asReadonly();

    private readonly estadoDaFilaSignal = signal<EEstadoDaFila>(EEstadoDaFila.CONFERINDO);
    /** Onde a fila está agora. */
    readonly estadoDaFila = this.estadoDaFilaSignal.asReadonly();

    /** Quantos itens da seleção original saíram do lote via `removerPendente`, antes de a fila chegar a eles. */
    private readonly foraDoLoteSignal = signal(0);

    /**
     * Resumo do lote para o diálogo. Não é um mero `itens().length`: itens removidos por
     * `removerPendente` saem do array, e o resumo precisa continuar contando o total original.
     *
     * <p>Rodada de correção 2 (Importante 6): o agregado passou a separar `presos` de `falharam`,
     * pela mesma regra que a tela já aplicava linha a linha (`FALHOU` + `aceitoNoPortal` é o item
     * finalizado no portal e parado no quadro, não uma falha). Somados, o cabeçalho contradizia a
     * lista logo abaixo dele — e o texto copiado, que é o artefato que sobrevive ao fechamento,
     * saía com um número que nenhuma de suas seções explicava.</p>
     */
    readonly resumo = computed<ResumoDoLote>(() => {
        const itens = this.itensSignal();
        const naoEncerrados = itens.filter((item) => item.estado === EEstadoDoItemDoLote.FALHOU);
        return {
            total: itens.length + this.foraDoLoteSignal(),
            concluidos: itens.filter((item) => item.estado === EEstadoDoItemDoLote.CONCLUIDO || item.estado === EEstadoDoItemDoLote.CONCLUIDO_COM_PENDENCIAS).length,
            comPendencias: itens.filter((item) => item.estado === EEstadoDoItemDoLote.CONCLUIDO_COM_PENDENCIAS).length,
            falharam: naoEncerrados.filter((item) => !item.aceitoNoPortal).length,
            presos: naoEncerrados.filter((item) => item.aceitoNoPortal).length,
            foraDoLote: this.foraDoLoteSignal()
        };
    });

    private readonly motivoDaParadaSignal = signal<EMotivoDaParada | null>(null);
    /**
     * Por que a fila parou, ou `null` enquanto ela não parou. **Publicado**, e não privado como era: a
     * distinção entre "o analista mandou parar" e "a sessão caiu" muda o que a tela diz e o que ela
     * oferece, e o único lugar que enxerga o 401 é aqui. Um espelho local no diálogo seria uma segunda
     * verdade sobre o mesmo fato — e divergiria na primeira parada que não tivesse saído de lá.
     */
    readonly motivoDaParada = this.motivoDaParadaSignal.asReadonly();

    private config: IniciarLoteConfig | null = null;
    /** Lido só no INÍCIO do próximo item do `concatMap` (ou de um `repetir`) — nunca cancela o item em andamento. */
    private paradaSolicitada = false;
    /**
     * Rodada de correção 2: `true` do instante em que uma chamada ao portal começa (fila automática OU
     * `repetir` manual) até ela terminar — sucesso, erro, ou cancelada — via `finalize()`. Existe
     * porque `estadoDaFila` sozinho NÃO responde com segurança "há uma chamada em voo agora": `parar()`
     * muda o estado para `PARADA` sem esperar o item corrente terminar (ver doc de `parar`), então um
     * `RODANDO` nominal também não é suficiente do outro lado (nada garante que o estado já refletiu a
     * chamada que acabou de começar). É esta flag, não o estado nominal, que `iniciar` e `repetir`
     * checam antes de tocar o portal de novo.
     *
     * <p>Rodada de correção 3: a trava em si nunca teve teste na LIBERAÇÃO, só na guarda — nenhum
     * teste disparava um `repetir` bem-sucedido e depois checava se um `iniciar`/`repetir` seguinte
     * ainda alcançava o portal. Um `repetir` que dá certo deixaria esta flag presa em `true` para
     * sempre (o `finalize()` que a apaga vive só no `fluxo` de `repetir`, não em outro lugar), travando
     * o serviço inteiro pelo resto da sessão — sem nada visível na tela além de um `logger.warn` a
     * cada tentativa seguinte. Ver o teste "libera chamadaEmVoo depois de repetir bem-sucedido".</p>
     *
     * <p>Correção final (Crítico 5): virou SINAL, e publicado. O diálogo precisa desta resposta para
     * decidir se pode ser fechado — fechar com uma chamada em voo recarrega o quadro antes de o
     * desfecho existir, e o protocolo volta à listagem como se nada tivesse acontecido. Um espelho no
     * diálogo seria uma segunda verdade sobre um fato que só a fila enxerga; um campo comum não
     * notificaria a tela. Sinal aqui resolve os dois.</p>
     */
    private readonly chamadaEmVooSignal = signal(false);
    /** Alguma chamada ao portal deste lote está aberta agora — a fila automática OU um `repetir`. */
    readonly chamadaEmVoo = this.chamadaEmVooSignal.asReadonly();
    private assinaturaDaFila?: Subscription;

    /**
     * O dono da fila morreu (navegação para uma rota irmã, por exemplo): a fila PARA, e não é
     * cancelada.
     *
     * A escolha entre as duas não é de gosto. `unsubscribe()` abortaria o XHR do item em voo, e o
     * portal pode já ter comitado a finalização dele — o cliente ficaria sem saber, que é exatamente
     * o estado que `aceitoNoPortal` existe para nunca produzir. Pior: se o `unsubscribe` caísse entre
     * o `finalizarSolicitacao` aceito e o `mover`, o protocolo terminaria finalizado no portal, sem
     * movimento no quadro e sem ninguém para contar a história. `parar()` deixa o item corrente
     * chegar ao fim — inclusive gravando o movimento — e transforma todos os seguintes em no-op, que
     * é o que de fato importa: nenhuma chamada NOVA sai depois da destruição.
     *
     * Não sobra assinatura pendurada por causa disso: com `paradaSolicitada` em `true`, cada item
     * restante do `concatMap` vira `of(void 0)` e o `from` esgota assim que o item em voo resolve —
     * o `complete` fecha a assinatura sozinho.
     *
     * <p>O que ISTO não resolve, e está registrado de propósito: voltar ao dashboard cria uma
     * instância nova do serviço, e a janela em que a fila velha ainda tem UM item em voo pode se
     * sobrepor ao primeiro item de um lote novo. Fechá-la exigiria uma trava fora do escopo do
     * componente; cancelar o item em voo para fechá-la trocaria um risco raro por um dano
     * irreversível, que é a troca errada.</p>
     */
    ngOnDestroy(): void {
        this.parar();
    }

    /**
     * Abre o lote e começa a processá-lo imediatamente, um protocolo por vez. `concatMap` é o que
     * garante a serialização: o próximo item só entra depois que a chamada ao portal (e o `mover`
     * seguinte) do item atual chegarem ao fim — sucesso ou falha.
     *
     * Recusa reentrada (Importante 6) enquanto houver `chamadaEmVoo`: chamar `iniciar` com uma chamada
     * ao portal ainda aberta desmontaria a memória de `aceitoNoPortal` de um item que o portal já pode
     * ter aceitado — essa memória é exatamente o que existe para uma finalização no portal nunca se
     * repetir.
     *
     * <p>Rodada de correção 2 (Importante A): a guarda checava `estadoDaFila === RODANDO`, mas
     * `parar()` põe a fila em `PARADA` com o item corrente ainda em voo — então um `parar()` seguido
     * de `iniciar()` passava direto pela guarda antiga, abortava o XHR em andamento (que o portal pode
     * já ter comitado) e zerava `aceitoNoPortal` do lote inteiro. `chamadaEmVoo` fecha essa porta
     * porque não depende do estado nominal da fila.</p>
     */
    iniciar(config: IniciarLoteConfig): void {
        if (this.chamadaEmVooSignal()) {
            this.logger.warn('iniciar() recusado: ainda há uma chamada ao portal em andamento.');
            return;
        }

        this.assinaturaDaFila?.unsubscribe();

        this.config = config;
        this.paradaSolicitada = false;
        this.motivoDaParadaSignal.set(null);
        this.foraDoLoteSignal.set(0);
        this.itensSignal.set(config.itens.map((item) => this.criarItemPendente(item, config)));
        this.estadoDaFilaSignal.set(EEstadoDaFila.RODANDO);

        this.assinaturaDaFila = from(config.itens)
            .pipe(concatMap((item) => this.processarItem(item, config)))
            .subscribe({
                complete: () => {
                    // Uma parada (manual ou por 401) já deixou a fila em 'parada' antes de o
                    // `from` esgotar os itens restantes (cada um vira um no-op). Só marca
                    // 'encerrada' quem correu até o fim sem ser interrompido.
                    if (this.estadoDaFilaSignal() === EEstadoDaFila.RODANDO) {
                        this.estadoDaFilaSignal.set(EEstadoDaFila.ENCERRADA);
                    }
                },
                error: (erro: unknown) => {
                    // Rede de segurança: cada item já se recupera com catchError, então isto não
                    // deveria disparar — mas sem um handler aqui, uma falha inesperada na própria
                    // orquestração deixaria a fila morta, calada, em 'rodando' para sempre.
                    this.logger.error('A fila do encerramento em lote quebrou de forma inesperada.', erro);
                    this.paradaSolicitada = true;
                    this.motivoDaParadaSignal.set(EMotivoDaParada.FALHA_INESPERADA);
                    this.estadoDaFilaSignal.set(EEstadoDaFila.PARADA);
                }
            });
    }

    /**
     * Interrompe a fila depois do item atual — nunca no meio dele — e bloqueia `repetir` daqui em
     * diante: é um "não toque mais neste lote", diferente de uma sessão que caiu sozinha. Por isso é
     * alcançável tanto com a fila `RODANDO` quanto já `PARADA` por 401 (o analista pode ter feito
     * vários `repetir` manuais nesse meio-tempo e só então decidir desistir do lote inteiro) — só é
     * no-op quando não há nada em curso nem por vir (`CONFERINDO`/`ENCERRADA`).
     */
    parar(): void {
        const estado = this.estadoDaFilaSignal();
        if (estado === EEstadoDaFila.CONFERINDO || estado === EEstadoDaFila.ENCERRADA) {
            return;
        }

        this.paradaSolicitada = true;
        this.motivoDaParadaSignal.set(EMotivoDaParada.PEDIDO_DO_ANALISTA);
        this.estadoDaFilaSignal.set(EEstadoDaFila.PARADA);
    }

    /**
     * Tira um item ainda `PENDENTE` do lote antes de a fila chegar a ele. O nome não é mais "antes de
     * iniciar": antes do primeiro `iniciar()` a lista está vazia, então o efeito real só existe
     * DEPOIS — com a fila já povoada, rodando, parada ou encerrada. Item que já começou a executar
     * (ou já terminou) não é removido: a ação pode já ter saído para o portal, e isso não se desfaz
     * apagando a linha da tela.
     */
    removerPendente(chave: string): void {
        const atual = this.itensSignal().find((item) => item.chave === chave);
        if (!atual) {
            return;
        }
        if (atual.estado !== EEstadoDoItemDoLote.PENDENTE) {
            this.logger.warn('Tentativa de remover do lote um item que já começou a ser processado:', chave);
            return;
        }

        this.itensSignal.update((itens) => itens.filter((item) => item.chave !== chave));
        this.foraDoLoteSignal.update((total) => total + 1);
    }

    /**
     * Refaz um item em dois níveis, e confundi-los reabriria uma finalização já aceita: se o portal
     * JÁ aceitou (`aceitoNoPortal`), repete só o `mover` — o backend é idempotente quando a
     * solicitação já está no destino. Senão, refaz a dupla completa (consultar+finalizar e mover).
     *
     * Recusa (Crítico 1) enquanto QUALQUER chamada deste lote estiver em voo — a fila automática OU
     * outro `repetir` — porque duas efetivações do MESMO lote em paralelo disputariam o mesmo
     * `MAX(NU_CRM)+1`, exatamente o que a serialização existe para evitar; `repetir` é uma segunda
     * porta de entrada para o mesmo risco, e precisa da mesma trava. Também recusa depois de um
     * `parar()` explícito (`motivoDaParada === PEDIDO_DO_ANALISTA`), mas NÃO depois de uma parada por
     * 401: a sessão pode ter voltado, e bloquear ali para sempre tornaria `repetir` inútil no cenário
     * para o qual existe.
     *
     * <p>Rodada de correção 2 (Importante A): a guarda usava `estadoDaFila === RODANDO` para detectar
     * "outra chamada em voo", mas isso tinha o mesmo furo do `iniciar` — `parar()` põe a fila em
     * `PARADA` com uma chamada ainda aberta. Trocado por `chamadaEmVoo`.</p>
     *
     * <p>Rodada de correção 2 (Importante B, defeito introduzido pela própria correção do Importante 7
     * da rodada 1): `estadoDaFilaAntes` era restaurado incondicionalmente no `complete`/`error` deste
     * método — inclusive quando a PRÓPRIA chamada deste `repetir` falhava com 401 e `tratarFalhaDeFinalizacao`
     * (chamado por dentro de `executarDoZero`) já tinha acabado de pôr a fila em `PARADA`: como esse
     * catchError interno devolve `of(void 0)`, o `fluxo` completa normalmente por fora, e o `complete`
     * daqui sobrescrevia a `PARADA` recém-decidida de volta para o estado antigo (ex.: `ENCERRADA`) —
     * desligando bem no `repetir` a proteção que a parada por 401 existe para dar. A correção: só
     * restaurar quando o estado ainda for o que foi capturado (`RODANDO`, setado por este método) —
     * se algo mudou o estado DURANTE o fluxo (como o 401), essa mudança já é mais recente e não pode
     * ser pisada por uma restauração de um valor mais velho.</p>
     */
    repetir(chave: string): void {
        const atual = this.itensSignal().find((item) => item.chave === chave);
        const config = this.config;
        if (!atual || !config) {
            this.logger.warn('Repetir chamado para uma chave fora do lote atual:', chave);
            return;
        }
        if (atual.estado === EEstadoDoItemDoLote.EXECUTANDO || this.chamadaEmVooSignal() || this.motivoDaParadaSignal() === EMotivoDaParada.PEDIDO_DO_ANALISTA) {
            this.logger.warn('Repetir recusado: outra chamada ao portal está em andamento, ou o lote foi parado manualmente.', chave);
            return;
        }

        const estadoDaFilaAntes = this.estadoDaFilaSignal();
        this.estadoDaFilaSignal.set(EEstadoDaFila.RODANDO);
        this.atualizarItem(chave, { estado: EEstadoDoItemDoLote.EXECUTANDO, erro: null });
        this.chamadaEmVooSignal.set(true);

        const fluxo = (atual.aceitoNoPortal ? this.moverEFinalizar(chave, atual.item, atual.pendencias, config) : this.executarDoZero(chave, atual.item, config)).pipe(finalize(() => this.chamadaEmVooSignal.set(false)));

        /**
         * Só restaura `estadoDaFilaAntes` quando a fila AINDA estiver `RODANDO` — o valor que este
         * método mesmo setou logo acima. Se mudou (o único jeito é uma parada por 401 decidida dentro
         * do próprio fluxo, via `tratarFalhaDeFinalizacao`), essa decisão é mais recente e não pode
         * ser sobrescrita por uma restauração de um valor mais velho.
         *
         * <p>Rodada de correção 3 (Menor — ramo morto): o `this.pausaManual ? PARADA : ...` daqui
         * chegou a existir, mas o ramo `PARADA` é inalcançável — a parada manual (hoje
         * `motivoDaParada === PEDIDO_DO_ANALISTA`) só é gravada dentro de `parar()`, que SEMPRE grava
         * `PARADA` na mesma chamada síncrona; não há como ela valer aqui com a fila ainda `RODANDO` (a
         * checagem acima já teria sido `false` e pulado a restauração). Simplificado para refletir isso.</p>
         */
        const restaurarSeAindaRodando = (): void => {
            if (this.estadoDaFilaSignal() === EEstadoDaFila.RODANDO) {
                this.estadoDaFilaSignal.set(estadoDaFilaAntes);
            }
        };

        fluxo.subscribe({
            complete: restaurarSeAindaRodando,
            error: (erro: unknown) => {
                // Rede de segurança sem caminho conhecido hoje: cada passo interno já se recupera com
                // catchError (e `detectarPendenciasSeNecessario` ainda embrulha em `defer()` para
                // blindar até um throw SÍNCRONO do registry de consulta). O que escaparia de tudo isso
                // é uma dependência que quebrasse o próprio contrato de devolver `Observable` — ex.:
                // `facade.finalizarSolicitacao` ou `kanbanServicosService.mover` lançando síncrono em
                // vez de devolver um Observable que erra — porque, ao contrário de
                // `detectarPendenciasSeNecessario`, nenhuma chamada aqui usa `defer()`. Sem este
                // handler, o item ficaria 'executando' para sempre, travado pela própria guarda desta
                // função.
                this.logger.error('Repetir falhou de forma inesperada.', erro);
                this.atualizarItem(chave, { estado: EEstadoDoItemDoLote.FALHOU, erro: this.customService.extractBackendErrorMessage(erro, 'Falha ao repetir o item.') });
                restaurarSeAindaRodando();
            }
        });
    }

    /**
     * `aceitoNoPortal` NASCE consultando a memória compartilhada, e não em `false`.
     *
     * Correção final (Crítico 4): nascendo em `false`, um protocolo que ficou preso num lote anterior
     * (ou no fluxo unitário do quadro) e foi reincluído num lote novo seria mandado ao portal outra
     * vez — a Regra 2 violada, ato administrativo duplicado. O campo continua no item porque a tela o
     * lê linha a linha, mas ele é PROJEÇÃO da memória do `ConclusoesNoPortalService`, nunca uma
     * segunda verdade: só `registrarAceiteNoPortal` o escreve, e sempre junto do registro lá.
     */
    private criarItemPendente(item: ItemStatus, config: IniciarLoteConfig): ItemDoLote {
        return { chave: chaveProtocolo(item), item, estado: EEstadoDoItemDoLote.PENDENTE, erro: null, aceitoNoPortal: this.jaAceitoNoPortal(item, config), pendencias: [] };
    }

    /**
     * Como o lote nomeia um protocolo dentro da memória de aceites — a mesma tripla que o quadro usa
     * (`KanbanProtocolos.chaveDoAceite`).
     *
     * Recebe o `ItemStatus`, e não a chave já pronta, porque a UF faz parte do nome e só o item a
     * carrega. Resolvida por `ufDoItem`, que é a MESMA resolução do quadro (própria do protocolo, a do
     * lote como fallback): um dos dois lados usando `item.ufCrm` cru faria o lote gravar sob `''` e o
     * quadro reler sob `'RJ'` — a proteção contra a segunda finalização desligada, sem sintoma.
     */
    private chaveDoAceite(item: ItemStatus, config: IniciarLoteConfig): string {
        return chaveDaConclusao(config.codigoServico, this.ufDoItem(item, config), chaveProtocolo(item));
    }

    private jaAceitoNoPortal(item: ItemStatus, config: IniciarLoteConfig): boolean {
        return this.conclusoesNoPortal.acaoAceita(this.chaveDoAceite(item, config)) !== null;
    }

    /**
     * Registra o aceite do portal nos dois lugares que o leem — a memória compartilhada (dona do fato,
     * e a única que o quadro enxerga) e a linha do item na tela — num ponto só. Separados, bastaria um
     * caminho novo escrever um e esquecer o outro para o lote voltar a divergir do quadro, que é
     * precisamente o defeito que esta correção fecha.
     */
    private registrarAceiteNoPortal(item: ItemStatus, pendencias: string[], config: IniciarLoteConfig): void {
        this.conclusoesNoPortal.registrar(this.chaveDoAceite(item, config), config.acao);
        this.atualizarItem(chaveProtocolo(item), { aceitoNoPortal: true, pendencias });
    }

    /**
     * Ponto de entrada do `concatMap`: item removido ou fila parada viram no-op, sem tocar o portal —
     * e por isso NÃO acendem `chamadaEmVoo` (não há chamada nenhuma para esperar). `finalize()` (não
     * `tap`/`catchError`) apaga a flag porque precisa rodar tanto no sucesso quanto no erro E numa
     * eventual cancelada (`unsubscribe`), e é o único operador dos três que cobre os três casos.
     *
     * Item que o portal JÁ aceitou antes deste lote (`aceitoNoPortal` semeado por `criarItemPendente`)
     * pula a finalização e vai direto ao `mover`, como faz o `repetir`: o que falta nele é só o
     * movimento, e refazer a finalização seria o segundo ato administrativo. Sem pendências: as do
     * lote anterior não sobrevivem à sessão, e inventar uma lista aqui seria pior que não ter nenhuma.
     */
    private processarItem(item: ItemStatus, config: IniciarLoteConfig): Observable<void> {
        const chave = chaveProtocolo(item);
        if (this.paradaSolicitada || !this.aindaNoLote(chave)) {
            return of(void 0);
        }

        this.atualizarItem(chave, { estado: EEstadoDoItemDoLote.EXECUTANDO });
        this.chamadaEmVooSignal.set(true);
        const fluxo = this.jaAceitoNoPortal(item, config) ? this.moverEFinalizar(chave, item, [], config) : this.executarDoZero(chave, item, config);
        return fluxo.pipe(finalize(() => this.chamadaEmVooSignal.set(false)));
    }

    private aindaNoLote(chave: string): boolean {
        return this.itensSignal().some((item) => item.chave === chave);
    }

    /**
     * A dupla completa de um item, do zero: detecta pendências (quando a ação é efetivar e o serviço
     * tem detecção conhecida), chama `facade.finalizarSolicitacao` — NUNCA os métodos `void`
     * `confirmarSolicitacao*`/`cancelarSolicitacao*`, que compartilham uma única `Subscription` e
     * fariam um item cancelar o anterior — e, só depois do portal aceitar, grava o movimento.
     * `concatMap` (não `switchMap`) em cada passo: num arquivo cuja razão de existir é nunca chamar o
     * portal duas vezes, `switchMap` é o operador que cancela e redispara — não há hoje um novo valor
     * chegando a estas fontes, mas é a porta errada para deixar aberta. Nunca deixa o erro escapar: um
     * item que falha não pode derrubar o `concatMap` da fila inteira.
     *
     * <p>Onda de correção final (Crítico 1): guarda de `somenteLeitura()` aqui, e em `moverEFinalizar`
     * — nenhum outro lugar. Verificado com grep em `.mover(` e `finalizarSolicitacao(` no módulo
     * `features/servicos` inteiro: este método é o ÚNICO chamador de `finalizarSolicitacao` do
     * arquivo, e junto com `moverEFinalizar` (único chamador de `.mover(`) fecha as duas vias de
     * entrada que existem hoje — a fila automática do `iniciar` (via `processarItem`) e um `repetir`
     * manual — porque nenhuma das duas tem outro caminho até o portal além destes dois métodos.</p>
     */
    private executarDoZero(chave: string, item: ItemStatus, config: IniciarLoteConfig): Observable<void> {
        if (this.ufEfetivaStore.somenteLeitura()) {
            this.atualizarItem(chave, { estado: EEstadoDoItemDoLote.FALHOU, erro: ERRO_SOMENTE_LEITURA });
            return of(void 0);
        }

        return this.detectarPendenciasSeNecessario(item, config).pipe(
            concatMap((pendencias) =>
                this.facade.finalizarSolicitacao(this.requestDoProtocolo(item, config), config.acao, config.motivo).pipe(
                    tap(() => this.registrarAceiteNoPortal(item, pendencias, config)),
                    concatMap(() => this.moverEFinalizar(chave, item, pendencias, config)),
                    catchError((erro: unknown) => {
                        this.tratarFalhaDeFinalizacao(chave, erro);
                        return of(void 0);
                    })
                )
            )
        );
    }

    /**
     * O DTO consultado pelo portal chega no primeiro round-trip de cada item — o mesmo
     * `consultarDadosProtocoloPorServico` que a tela unitária já usa para popular o card —, então a
     * detecção não pede um endpoint novo. Só roda na efetivação, e só para serviço com detecção
     * VERIFICADA na fonte (`servicoTemDeteccaoDePendencias`) — cancelamento não registra ninguém, e
     * serviço não verificado não ganha pendência chutada. `defer` embrulha a chamada porque o
     * registry por trás dela (`consultarProtocoloPorCodigoServico`) lança SÍNCRONO para
     * `codigoServico` não mapeado — sem `defer`, isso escaparia do `catchError` abaixo. Falha na
     * consulta não bloqueia a efetivação: vira zero pendências reportadas, nunca um item 'falhou' por
     * causa de uma checagem informativa.
     */
    private detectarPendenciasSeNecessario(item: ItemStatus, config: IniciarLoteConfig): Observable<string[]> {
        if (config.acao !== 'efetivar' || !servicoTemDeteccaoDePendencias(config.codigoServico)) {
            return of([]);
        }

        return defer(() => this.facade.consultarDadosProtocoloPorServico(config.codigoServico, this.requestDeConsulta(item, config))).pipe(
            map((dto) => detectarPendencias(config.codigoServico, dto)),
            catchError(() => of([]))
        );
    }

    /**
     * Grava o movimento do item já aceito no portal. Único ponto de saída bem-sucedida do item:
     * decide entre `CONCLUIDO`/`CONCLUIDO_COM_PENDENCIAS` a partir das pendências já detectadas — a
     * repetição de um item aceito reusa este método sem redetectar nada, porque a detecção só faz
     * sentido antes de o portal aceitar.
     *
     * Decisão consciente (não descuido): não há checagem de 401 aqui. Na finalização o 401 chega
     * intacto do backend; no `mover`, uma falha do PORTAL sai mascarada como 502 com título fixo —
     * checar 401 aqui seria placebo. A parada automática da fila por sessão expirada cobre só a
     * finalização; é uma parada PARCIAL, e é intencional.
     *
     * O par fechou: a memória do aceite sai do `ConclusoesNoPortalService` AQUI, e só aqui — antes
     * disso ela é a garantia contra a segunda finalização, no lote e no quadro.
     *
     * <p>Onda de correção final (Crítico 1): guarda de `somenteLeitura()` também aqui, e não só em
     * `executarDoZero` — um item já aceito no portal (`processarItem`/`repetir` com `aceitoNoPortal`)
     * chama este método DIRETO, pulando `executarDoZero` inteiro. Ver o comentário lá para o
     * inventário completo dos dois pontos.</p>
     */
    private moverEFinalizar(chave: string, item: ItemStatus, pendencias: string[], config: IniciarLoteConfig): Observable<void> {
        if (this.ufEfetivaStore.somenteLeitura()) {
            this.atualizarItem(chave, { estado: EEstadoDoItemDoLote.FALHOU, erro: ERRO_SOMENTE_LEITURA });
            return of(void 0);
        }

        const request = this.requestDeMovimento(item, config);
        if (!request) {
            this.atualizarItem(chave, { estado: EEstadoDoItemDoLote.FALHOU, erro: ERRO_IDENTIDADE_INVALIDA });
            return of(void 0);
        }

        return this.kanbanServicosService.mover(request).pipe(
            tap(() => {
                this.conclusoesNoPortal.esquecer(this.chaveDoAceite(item, config));
                this.atualizarItem(chave, { estado: pendencias.length > 0 ? EEstadoDoItemDoLote.CONCLUIDO_COM_PENDENCIAS : EEstadoDoItemDoLote.CONCLUIDO });
            }),
            map(() => void 0),
            catchError((erro: unknown) => {
                this.atualizarItem(chave, { estado: EEstadoDoItemDoLote.FALHOU, erro: this.customService.extractBackendErrorMessage(erro, 'Falha ao mover o protocolo no quadro.') });
                return of(void 0);
            })
        );
    }

    /**
     * Marca o item como falho e, só quando o erro é 401, para a fila inteira — sessão expirada não
     * se resolve tentando o próximo item, e continuar mandaria mais chamadas fadadas ao mesmo erro.
     */
    private tratarFalhaDeFinalizacao(chave: string, erro: unknown): void {
        this.atualizarItem(chave, { estado: EEstadoDoItemDoLote.FALHOU, erro: this.customService.extractBackendErrorMessage(erro, 'Falha ao finalizar a solicitação no portal.') });

        if (erro instanceof HttpErrorResponse && erro.status === 401) {
            this.paradaSolicitada = true;
            this.motivoDaParadaSignal.set(EMotivoDaParada.SESSAO_EXPIRADA);
            this.estadoDaFilaSignal.set(EEstadoDaFila.PARADA);
        }
    }

    private requestDeConsulta(item: ItemStatus, config: IniciarLoteConfig): { numeroProtocolo: string; anoProtocolo: string; ufCrm: string } {
        return {
            numeroProtocolo: item.numeroProtocolo ?? '',
            anoProtocolo: item.anoProtocolo ?? '',
            ufCrm: this.ufDoItem(item, config)
        };
    }

    private requestDoProtocolo(item: ItemStatus, config: IniciarLoteConfig): RequestDoProtocolo {
        return {
            numeroProtocolo: item.numeroProtocolo ?? '',
            anoProtocolo: item.anoProtocolo ?? '',
            ufCrm: this.ufDoItem(item, config),
            codigoServico: config.codigoServico
        };
    }

    /** `MoverSolicitacaoRequest` a partir do item e da coluna destino; `null` quando número/ano não são inteiros válidos. */
    private requestDeMovimento(item: ItemStatus, config: IniciarLoteConfig): MoverSolicitacaoRequest | null {
        const numero = paraInteiro(item.numeroProtocolo);
        const ano = paraInteiro(item.anoProtocolo);
        if (numero === null || ano === null) {
            return null;
        }

        return {
            idServico: config.idServico,
            numero,
            ano,
            uf: this.ufDoItem(item, config),
            idStatusDestino: config.colunaDestino.status.id,
            // A ação do lote é única para a fila inteira (`config.acao`) e é a que o portal executou
            // item a item, logo antes deste `mover`. Mesma tradução do caminho unitário, de
            // propósito: um lote que gravasse a letra oposta inverteria o relatório em massa.
            desfecho: desfechoDaAcao(config.acao)
        };
    }

    /**
     * UF do protocolo, com a do quadro como fallback — o MESMO `protocolo.ufCrm ?? uf do quadro` que
     * `KanbanProtocolos.ufDoProtocolo` e `EncerramentoLote.ufDoProtocolo` já faziam.
     *
     * <p>Rodada de correção 2 (Importante 1): o fallback não existia aqui, e as consequências eram
     * duas. A consulta e a finalização de um item sem `ufCrm` próprio saíam ao portal com `ufCrm: ''`
     * — a consulta é POR UF, então o portal responderia sobre outra coisa ou nada. E a memória de
     * aceites não podia incluir a UF na chave enquanto este método divergisse do quadro: o lote
     * gravaria sob `''`, o quadro releria sob `'RJ'`, e a proteção contra a segunda finalização
     * ficaria desligada sem nenhum sintoma. Corrigido o fallback, a UF entrou na chave.</p>
     *
     * A UF vem do `config`, e não de um sinal de tela, porque a fila roda ao longo do tempo e o
     * contexto tem de viajar como dado — mesma razão do resto do `IniciarLoteConfig`.
     */
    private ufDoItem(item: ItemStatus, config: IniciarLoteConfig): string {
        return ((item.ufCrm ?? '').trim() || config.uf.trim()).toUpperCase();
    }

    private atualizarItem(chave: string, patch: Partial<ItemDoLote>): void {
        this.itensSignal.update((itens) => itens.map((item) => (item.chave === chave ? { ...item, ...patch } : item)));
    }
}
